import { pathMatchesRoute } from "@faire/web--source/common/routes/RouteMatcher";
import { isPrerender } from "@faire/web--source/common/user-agent/isPrerender";
import { Column } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackLoggedOutRetailersLoggedOutHomeRedesignCtaClick } from "@faire/web-api--source/events/loggedOutRetailers/click/loggedOutHomeRedesignCta";
import { trackLoggedOutRetailersLoggedOutHomeRedesignView } from "@faire/web-api--source/events/loggedOutRetailers/view/loggedOutHomeRedesign";
import { ICategoryBrandsCarousel } from "@faire/web-api--source/indigofair/data/ICategoryBrandsCarousel";
import { BATCH_5_HOMEPAGE_FIRST_SECTION_COPY_TEST_102124 } from "@faire/web-api--source/indigofair/settings/BATCH_5_HOMEPAGE_FIRST_SECTION_COPY_TEST_102124";
import { IFourVariantXP } from "@faire/web-api--source/indigofair/settings/IFourVariantXP";
import { route as brandRoute } from "@faire/web-api--source/routes/brand/brandToken";
import classNames from "classnames";
import React from "react";
import { useLocation } from "react-router-dom";

import { SlideIn } from "@faire/retailer/components/SlideIn";
import { setTimeoutIfPossible } from "@faire/retailer/lib/async-functions/setTimeoutIfPossible";
import { handleKeyDown } from "@faire/retailer/lib/handleKeyDown";
import { useEffectAssignWhen } from "@faire/retailer/settings/useEffectAssignWhen";
import { useSettingHomePageFirstSectionCopyTestB5 } from "@faire/retailer/settings/useSettingHomePageFirstSectionCopyTestB5";
import * as animationStyles from "@faire/retailer/views/home/logged-out-homepage/__internal__/components/__internal__/Animation/animation.css";
import { useAnimatedScroll } from "@faire/retailer/views/home/logged-out-homepage/__internal__/components/__internal__/Animation/useAnimatedScroll";
import { invisible } from "@faire/retailer/views/home/logged-out-homepage/__internal__/components/__internal__/Prerender/style.css";
import * as tabStyles from "@faire/retailer/views/home/logged-out-homepage/__internal__/components/__internal__/Tabs/tabs.css";
import { CategoryBestBrandsWithCTA } from "@faire/retailer/views/home/logged-out-homepage/__internal__/components/CategoryBestBrandsBanner/__internal__/CategoryBestBrandsWithCTA";
import * as componentStyles from "@faire/retailer/views/home/logged-out-homepage/__internal__/components/CategoryBestBrandsBanner/styles.css";
import { useCategoryBrandsBannerQuery } from "@faire/retailer/views/home/logged-out-homepage/__internal__/DataFetching/useCategoryBrandsBannerQuery";

export const CategoryBestBrandsBanner: React.FC<{
  loadTimingRef?: React.RefCallback<HTMLElement>;
}> = ({ loadTimingRef }) => {
  const { categoryBestBrands, isFetching } = useCategoryBrandsBannerQuery();

  const location = useLocation();
  const isBrandPage = pathMatchesRoute(location, brandRoute);

  useEffectAssignWhen(
    BATCH_5_HOMEPAGE_FIRST_SECTION_COPY_TEST_102124,
    !isBrandPage
  );

  const {
    resumeScroll,
    pauseScroll,
    isVisible,
    currentIndex,
    setCurrentIndex,
  } = useAnimatedScroll(categoryBestBrands?.length ?? 0, 5000);

  const selectedCategory = categoryBestBrands?.[currentIndex];

  const handleClickTab = (index: number, categoryName: string) => {
    trackLoggedOutRetailersLoggedOutHomeRedesignCtaClick(
      "category_best_brands_banner-tab",
      categoryName
    );
    pauseScroll();
    setCurrentIndex(index);
    setTimeoutIfPossible(() => {
      resumeScroll();
    }, 12000);
  };

  const tabRef = useDoActionWhenVisible(() =>
    trackLoggedOutRetailersLoggedOutHomeRedesignView(
      "category_best_brands_banner-tab"
    )
  );

  if (isFetching) {
    return <div style={{ height: "700px" }}></div>;
  }

  if (!categoryBestBrands?.length || !selectedCategory) {
    return null;
  }

  return (
    <Column className={componentStyles.container} ref={loadTimingRef}>
      <SlideIn bottomThreshold={100} offset={getSpacing("5x")}>
        <Column
          className={classNames(
            componentStyles.headerAndTabs,
            animationStyles.animatedContainerParent
          )}
        >
          <CategoryBestBrandsHeader isBrandPage={isBrandPage} />
          <Spacer height="2x" />
          <div className={tabStyles.tabBar} ref={tabRef}>
            {categoryBestBrands.map((category, index) => (
              <div
                key={index}
                onClick={() =>
                  handleClickTab(index, category.category_path_identifier ?? "")
                }
                onKeyDown={handleKeyDown(() =>
                  handleClickTab(index, category.category_path_identifier ?? "")
                )}
              >
                <Typography
                  tabIndex={0}
                  data-is-selected={index === currentIndex}
                  className={classNames(tabStyles.tab)}
                >
                  {category.category_name}
                </Typography>
              </div>
            ))}
          </div>
        </Column>

        <div
          style={{ overflow: "hidden", width: "100%" }}
          data-is-visible={isVisible}
          className={animationStyles.animatedContainer}
        >
          {isPrerender() ? (
            <PrerenderCategories categoryBestBrands={categoryBestBrands} />
          ) : (
            <CategoryBestBrandsWithCTA
              selectedCategory={selectedCategory}
              pauseScroll={pauseScroll}
              resumeScroll={resumeScroll}
            />
          )}
        </div>
      </SlideIn>
    </Column>
  );
};

const PrerenderCategories: React.FC<{
  categoryBestBrands: ICategoryBrandsCarousel[];
}> = ({ categoryBestBrands }) => (
  // we want to have all categories in the DOM for prerendering
  <>
    {categoryBestBrands.map((category, index) => (
      <CategoryBestBrandsWithCTA
        key={index}
        selectedCategory={category}
        className={index ? invisible : ""}
      />
    ))}
  </>
);

const CategoryBestBrandsHeader: React.FC<{ isBrandPage: boolean }> = ({
  isBrandPage,
}) => {
  const headerRef = useDoActionWhenVisible(() =>
    trackLoggedOutRetailersLoggedOutHomeRedesignView(
      "category_best_brands_banner-header"
    )
  );

  const settingHomePageFirstSectionCopyTestB5 =
    useSettingHomePageFirstSectionCopyTestB5();

  const newTitleVariantsOnHomepageOnly = isBrandPage
    ? IFourVariantXP.Bucket.CONTROL
    : settingHomePageFirstSectionCopyTestB5;

  const { isXLargeAndAbove } = useViewport();

  switch (newTitleVariantsOnHomepageOnly) {
    case IFourVariantXP.Bucket.VARIANT_2:
      return (
        <Typography
          variant={{
            mobileAndAbove: "displaySSerifRegular",
            desktopAndAbove: "displayMSerifRegular",
          }}
          ref={headerRef}
        >
          <StrictLocalMsg
            defaultMessage="Your one-stop shop for wholesale"
            description={{
              text: "category best brands header",
              img: "https://cdn.faire.com/fastly/6e3d6ad4d339041af47cf2ca812d7bb6b29b29287a87254f66e362da196a3947.png",
            }}
          />
        </Typography>
      );
    case IFourVariantXP.Bucket.VARIANT_3:
      if (isXLargeAndAbove) {
        return (
          <Typography
            variant={{
              mobileAndAbove: "displaySSerifRegular",
              desktopAndAbove: "displayMSerifRegular",
            }}
            ref={headerRef}
          >
            <StrictLocalMsg
              defaultMessage="Buy from any category, all in one place"
              description={{
                text: "category best brands header",
                img: "https://cdn.faire.com/fastly/32115d3cfbd70288ae1ad5f47c55c3a2b9852a69edd5ff55fcb9ef0b05cd536b.png",
              }}
            />
          </Typography>
        );
      }
      return (
        <>
          <Typography
            variant={{
              mobileAndAbove: "displaySSerifRegular",
              desktopAndAbove: "displayMSerifRegular",
            }}
            ref={headerRef}
          >
            <StrictLocalMsg
              defaultMessage="Buy from any category,"
              description={{
                text: "category best brands header",
                img: "https://cdn.faire.com/fastly/32115d3cfbd70288ae1ad5f47c55c3a2b9852a69edd5ff55fcb9ef0b05cd536b.png",
              }}
            />
          </Typography>
          <Typography
            variant={{
              mobileAndAbove: "displaySSerifRegular",
              desktopAndAbove: "displayMSerifRegular",
            }}
          >
            <StrictLocalMsg
              defaultMessage="all in one place"
              description={{
                text: "category best brands header",
                img: "https://cdn.faire.com/fastly/32115d3cfbd70288ae1ad5f47c55c3a2b9852a69edd5ff55fcb9ef0b05cd536b.png",
              }}
            />
          </Typography>
        </>
      );
    case IFourVariantXP.Bucket.VARIANT_4:
      return (
        <Typography
          variant={{
            mobileAndAbove: "displaySSerifRegular",
            desktopAndAbove: "displayMSerifRegular",
          }}
          ref={headerRef}
        >
          <StrictLocalMsg
            defaultMessage="Stock your shelves with proven bestsellers"
            description={{
              text: "category best brands header",
              img: "https://cdn.faire.com/fastly/13c01a24be8ccc25c09d9aa2047eeb975205c6803893f93f24a8f7672c476660.png",
            }}
          />
        </Typography>
      );
    case IFourVariantXP.Bucket.CONTROL:
    default:
      return (
        <>
          <Typography
            variant={{
              mobileAndAbove: "displaySSerifRegular",
              desktopAndAbove: "displayMSerifRegular",
            }}
            ref={headerRef}
          >
            <StrictLocalMsg
              defaultMessage="The best brands for every shelf—"
              description={{
                text: "category best brands header",
                img: "https://cdn.faire.com/fastly/37eb9f81f6b526bff89f92a2430972f44ff937df777e3cee32069276f75e4fe7.png",
              }}
            />
          </Typography>
          <Typography
            variant={{
              mobileAndAbove: "displaySSerifRegular",
              desktopAndAbove: "displayMSerifRegular",
            }}
          >
            <StrictLocalMsg
              defaultMessage="find them on Faire"
              description={{
                text: "category best brands header",
                img: "https://cdn.faire.com/fastly/37eb9f81f6b526bff89f92a2430972f44ff937df777e3cee32069276f75e4fe7.png",
              }}
            />
          </Typography>
        </>
      );
  }
};
